import React, { useState } from "react";
import { Search, Plus, ChevronDown, SlidersHorizontal } from "lucide-react";
import VisitLogModal from "../../components/VisitLogModal";
import MobileFilterSheet from "../../components/MobileFilterSheet";
import MobileFloatingActionButton from "../common/MobileFloatingActionButton";
import MobileListActiveFilterChips from "./MobileListActiveFilterChips";
import {
  MOBILE_HEADER_CONTAINER_CLASS,
  MOBILE_HEADER_COUNT_BADGE_CLASS,
  MOBILE_HEADER_COUNT_HIGHLIGHT_CLASS,
  MOBILE_HEADER_COUNT_TEXT_CLASS,
  MOBILE_HEADER_SEARCH_ICON_CLASS,
  MOBILE_HEADER_SEARCH_WRAPPER_CLASS,
  MOBILE_HEADER_SECOND_ROW_CLASS,
  MOBILE_HEADER_SORT_GROUP_CLASS,
  MOBILE_HEADER_SORT_LABEL_CLASS,
  MOBILE_HEADER_TITLE_CLASS,
  MOBILE_HEADER_TOP_ROW_CLASS,
} from "../common/mobileHeaderTokens";
import {
  getMobileItemAttendanceLabel,
  getMobileItemClassLabel,
  getMobileItemVisitLabel,
  MOBILE_LIST_CHEVRON_CLASS,
  MOBILE_LIST_EMPTY_STATE_CLASS,
  MOBILE_LIST_ITEM_INFO_CLASS,
  MOBILE_LIST_ITEM_AVATAR_WRAPPER_CLASS,
  MOBILE_LIST_ITEM_CARD_CLASS,
  MOBILE_LIST_ITEM_INLINE_ROW_CLASS,
  MOBILE_LIST_INLINE_DIVIDER_CLASS,
  MOBILE_LIST_META_ATTENDANCE_TEXT_CLASS,
  MOBILE_LIST_META_VISIT_TEXT_CLASS,
  MOBILE_LIST_ITEM_SUBTEXT_CLASS,
  MOBILE_LIST_ITEM_TITLE_CLASS,
  MOBILE_LIST_STACK_CLASS,
  MOBILE_LIST_ROOT_CONTAINER_CLASS,
  MOBILE_LIST_SEARCH_INPUT_CLASS,
  MOBILE_LIST_SCROLL_AREA_CLASS,
  MOBILE_LIST_STICKY_HEADER_CLASS,
  getMobileActiveFilterStyle,
  getMobileActiveFilterChips,
  getMobileFilterTriggerButtonClass,
  getMobileListSortOptions,
} from "./mobileListUtils";

export default function MobileListScreen({
  isTeacher,
  isUserTeacher,
  searchTerm,
  setSearchTerm,
  sortType,
  setSortType,
  sortOrder,
  setSortOrder,
  filteredItems,
  openDetail,
  getDeptColorClass,
  selectedDepartment,
  userProfile,
  openRegisterModal,
  onTouchStart,
  onTouchMove,
  onListSwipeEnd,
  viewingLog,
  setViewingLog,
  onUpdateLog,
  onDeleteLog,
  faithFilter,
  setFaithFilter,
  attendanceFilter,
  setAttendanceFilter,
  genderFilter,
  setGenderFilter,
  theme,
}) {
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const themeColor = theme?.primaryHex || "#354F38";

  const activeFilterStyle = getMobileActiveFilterStyle(themeColor);
  const sortOptions = getMobileListSortOptions(isTeacher);
  const activeFilterChips = getMobileActiveFilterChips({ faithFilter, attendanceFilter, genderFilter });
  const hasActiveFilters = activeFilterChips.length > 0;

  const filterResetHandlers = {
    faith: () => setFaithFilter("all"),
    attendance: () => setAttendanceFilter("all"),
    gender: () => setGenderFilter("all"),
  };

  const handleResetFilterChip = (chipKey) => {
    const reset = filterResetHandlers[chipKey];
    if (reset) reset();
  };

  const handleClearAllFilters = () => {
    Object.values(filterResetHandlers).forEach((reset) => reset());
  };

  return (
    <div
      className={MOBILE_LIST_ROOT_CONTAINER_CLASS}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onListSwipeEnd}
    >
      <div className={MOBILE_LIST_STICKY_HEADER_CLASS}>
        <div className={MOBILE_HEADER_CONTAINER_CLASS}>
          <div className={MOBILE_HEADER_TOP_ROW_CLASS}>
            <div className="flex items-center gap-2 min-w-0">
              <span className={MOBILE_HEADER_TITLE_CLASS}>{isTeacher ? "교사 명단" : "학생 명단"}</span>
            </div>
            <div className={MOBILE_HEADER_COUNT_BADGE_CLASS}>
              <span className={MOBILE_HEADER_COUNT_TEXT_CLASS}>
                총 <span className={MOBILE_HEADER_COUNT_HIGHLIGHT_CLASS}>{filteredItems.length}</span>명
              </span>
            </div>
          </div>

          <div className={MOBILE_HEADER_SECOND_ROW_CLASS}>
            <div className={MOBILE_HEADER_SEARCH_WRAPPER_CLASS}>
              <Search className={MOBILE_HEADER_SEARCH_ICON_CLASS} size={14} />
              <input
                id={`mobile-list-search-${isTeacher ? "teacher" : "student"}`}
                name={`mobile_list_search_${isTeacher ? "teacher" : "student"}`}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`${isTeacher ? "교사" : "학생"} 검색`}
                className={MOBILE_LIST_SEARCH_INPUT_CLASS}
              />
            </div>
            <div className={MOBILE_HEADER_SORT_GROUP_CLASS}>
              <button
                onClick={() => setIsFilterSheetOpen(true)}
                className={getMobileFilterTriggerButtonClass(hasActiveFilters)}
                style={hasActiveFilters ? activeFilterStyle : {}}
                title="필터 및 정렬"
              >
                <SlidersHorizontal size={14} />
                <span className={MOBILE_HEADER_SORT_LABEL_CLASS}>필터</span>
              </button>
            </div>
          </div>

          {/* Quick Filter Chips */}
          {hasActiveFilters && (
            <MobileListActiveFilterChips
              chips={activeFilterChips}
              activeFilterStyle={activeFilterStyle}
              onResetChip={handleResetFilterChip}
              onClearAll={handleClearAllFilters}
            />
          )}
        </div>
      </div>

      <div
        className={MOBILE_LIST_SCROLL_AREA_CLASS}
      >
        <div className={MOBILE_LIST_STACK_CLASS}>
          {filteredItems.length === 0 ? (
            <div className={MOBILE_LIST_EMPTY_STATE_CLASS}>
              <p className="text-base font-bold">등록된 {isTeacher ? "교사" : "학생"}이 없습니다</p>
              <p className="text-xs mt-1">+ 버튼을 눌러 추가해보세요</p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => openDetail(item)}
                className={MOBILE_LIST_ITEM_CARD_CLASS}
              >
                <div className={MOBILE_LIST_ITEM_AVATAR_WRAPPER_CLASS}>
                  {item.photoUrl ? (
                    <img src={item.photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div
                      className={`w-full h-full flex items-center justify-center text-white font-black text-xl ${getDeptColorClass(
                        item.grade || item.charge
                      )}`}
                    >
                      {item.name.charAt(0)}
                    </div>
                  )}
                </div>

                <div className={MOBILE_LIST_ITEM_INFO_CLASS}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <h3 className={MOBILE_LIST_ITEM_TITLE_CLASS}>{item.name}</h3>
                    {!isTeacher && item.faithState && item.faithState !== "기존신자" && (
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-black text-white leading-none ${item.faithState === "초신자" ? "bg-amber-400" : "bg-gray-400"}`}>
                        {item.faithState === "초신자" ? "NEW" : item.faithState}
                      </span>
                    )}
                  </div>
                  <div className={MOBILE_LIST_ITEM_INLINE_ROW_CLASS}>
                    <span className={MOBILE_LIST_ITEM_SUBTEXT_CLASS}>
                      {getMobileItemClassLabel(item)}
                    </span>
                    <span className={MOBILE_LIST_INLINE_DIVIDER_CLASS}>·</span>
                    <span className={MOBILE_LIST_META_ATTENDANCE_TEXT_CLASS}>
                      출석 {getMobileItemAttendanceLabel(item)}
                    </span>
                    <span className={MOBILE_LIST_INLINE_DIVIDER_CLASS}>·</span>
                    <span className={MOBILE_LIST_META_VISIT_TEXT_CLASS}>
                      심방 {getMobileItemVisitLabel(item)}
                    </span>
                  </div>
                </div>

                <ChevronDown size={18} className={MOBILE_LIST_CHEVRON_CLASS} />
              </div>
            ))
          )}
        </div>
      </div>

      {(!isTeacher || (isTeacher && !isUserTeacher)) && (
        <MobileFloatingActionButton
          onClick={openRegisterModal}
          className={`${getDeptColorClass(selectedDepartment !== "All" ? selectedDepartment : userProfile?.deptName)} text-white`}
          ariaLabel={`${isTeacher ? "교사" : "학생"} 등록`}
          title={`${isTeacher ? "교사" : "학생"} 등록`}
        >
          <Plus size={28} strokeWidth={2.5} />
        </MobileFloatingActionButton>
      )}

      {viewingLog && (
        <VisitLogModal
          log={viewingLog}
          onClose={() => setViewingLog(null)}
          onUpdate={(updatedLog) => {
            if (onUpdateLog) onUpdateLog(updatedLog);
            setViewingLog(updatedLog);
          }}
          onDelete={(id) => {
            if (onDeleteLog) onDeleteLog(id);
            setViewingLog(null);
          }}
        />
      )}

      <MobileFilterSheet
        isOpen={isFilterSheetOpen}
        onClose={() => setIsFilterSheetOpen(false)}
        title={isTeacher ? "교사 필터" : "학생 필터"}
        sortValue={sortType}
        sortOptions={sortOptions}
        onChangeSort={setSortType}
        sortOrder={sortOrder}
        onChangeSortOrder={setSortOrder}
        // New Filters
        faithFilter={faithFilter}
        onChangeFaithFilter={setFaithFilter}
        attendanceFilter={attendanceFilter}
        onChangeAttendanceFilter={setAttendanceFilter}
        genderFilter={genderFilter}
        onChangeGenderFilter={setGenderFilter}
        themeColor={themeColor}
      />
    </div>
  );
}
